import { useEffect, useState, type SVGProps } from 'react'
import { useTranslation } from 'react-i18next'
import ListingForm, { type SellFormMode } from './ListingForm'
import SellPathCardIcon, { type SellPathCardIconKind } from './SellPathCardIcon'

type WizardStep = 'choose-imei' | 'choose-device' | 'form'

export type SellEntry = 'wizard' | 'super-fast'

function IconChevronRight(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m9 6 6 6-6 6" />
    </svg>
  )
}

function SellPathHero({ title, intro }: { title: string; intro?: string }) {
  return (
    <header className="sell-path-page__hero">
      <h2>{title}</h2>
      {intro ? <p className="sell-path-page__hint">{intro}</p> : null}
    </header>
  )
}

function PrimarySellCard({
  title,
  hint,
  iconKind,
  onClick,
}: {
  title: string
  hint?: string
  iconKind: SellPathCardIconKind
  onClick: () => void
}) {
  return (
    <button type="button" className="sell-path-card sell-path-card--primary" onClick={onClick}>
      <span className="sell-path-card__shine" aria-hidden="true" />
      <SellPathCardIcon kind={iconKind} />
      <span className="sell-path-card__content">
        <span className="sell-path-card__title">{title}</span>
        {hint ? <span className="sell-path-card__hint">{hint}</span> : null}
      </span>
      <span className="sell-path-card__arrow" aria-hidden="true">
        <IconChevronRight />
      </span>
    </button>
  )
}

function SecondarySellCard({
  title,
  hint,
  iconKind,
  onClick,
}: {
  title: string
  hint?: string
  iconKind: SellPathCardIconKind
  onClick: () => void
}) {
  return (
    <button type="button" className="sell-path-card sell-path-card--secondary" onClick={onClick}>
      <SellPathCardIcon kind={iconKind} />
      <span className="sell-path-card__content">
        <span className="sell-path-card__title">{title}</span>
        {hint ? <span className="sell-path-card__hint">{hint}</span> : null}
      </span>
    </button>
  )
}

interface SellPathWizardProps {
  entry: SellEntry
  token?: string | null
  userPreview?: { email?: string; full_name?: string } | null
  onCreated?: (listingId: number) => void
  onGuestAuthed?: (token: string, user: unknown) => void
  onFlashMessage?: (message: string) => void
  onOpenAccountSettings?: () => void
}

function stepForEntry(entry: SellEntry): WizardStep {
  return entry === 'super-fast' ? 'form' : 'choose-imei'
}

function modeForEntry(entry: SellEntry): SellFormMode | null {
  return entry === 'super-fast' ? 'smartphone-no-imei' : null
}

export default function SellPathWizard({
  entry,
  token = null,
  userPreview = null,
  onCreated,
  onGuestAuthed,
  onFlashMessage,
  onOpenAccountSettings,
}: SellPathWizardProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<WizardStep>(() => stepForEntry(entry))
  const [formMode, setFormMode] = useState<SellFormMode | null>(() => modeForEntry(entry))

  useEffect(() => {
    if (entry === 'super-fast') {
      setFormMode('smartphone-no-imei')
      setStep('form')
      return
    }
    setFormMode(null)
    setStep('choose-imei')
  }, [entry])

  function openForm(mode: SellFormMode) {
    setFormMode(mode)
    setStep('form')
  }

  function openAdvancedChooser() {
    setFormMode(null)
    setStep('choose-imei')
  }

  if (step === 'form' && formMode) {
    return (
      <ListingForm
        key={formMode}
        token={token}
        userPreview={userPreview}
        mode={formMode}
        onCreated={onCreated}
        onGuestAuthed={onGuestAuthed}
        onFlashMessage={onFlashMessage}
        onOpenAccountSettings={onOpenAccountSettings}
        onBack={
          entry === 'super-fast'
            ? openAdvancedChooser
            : () => {
                if (formMode === 'smartphone-no-imei' || formMode === 'basic-phone') {
                  setStep('choose-device')
                } else {
                  setStep('choose-imei')
                }
                setFormMode(null)
              }
        }
      />
    )
  }

  if (step === 'choose-device') {
    return (
      <section className="listing-form-section sell-path-wizard sell-path-wizard--modern">
        <div className="listing-form sell-path-panel sell-path-panel--modern">
          <span className="sell-path-panel__glow" aria-hidden="true" />
          <button type="button" className="sell-path-back" onClick={() => setStep('choose-imei')}>
            ← {t('listing.sellPathBack')}
          </button>
          <SellPathHero title={t('listing.sellPathDeviceTitle')} intro={t('listing.sellPathDeviceIntro')} />
          <div className="sell-path-grid sell-path-grid--inline" role="group" aria-label={t('listing.sellPathDeviceTitle')}>
            <PrimarySellCard
              title={t('listing.sellPathSmartphone')}
              hint={t('listing.sellPathSmartphoneHint')}
              iconKind="smartphone"
              onClick={() => openForm('smartphone-no-imei')}
            />
            <SecondarySellCard
              title={t('listing.sellPathBasicPhone')}
              hint={t('listing.sellPathBasicHint')}
              iconKind="basic-phone"
              onClick={() => openForm('basic-phone')}
            />
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="listing-form-section sell-path-wizard sell-path-wizard--modern">
      <div className="listing-form sell-path-panel sell-path-panel--modern">
        <span className="sell-path-panel__glow" aria-hidden="true" />
        <SellPathHero title={t('listing.sellPathTitle')} intro={t('listing.sellPathIntro')} />
        <div className="sell-path-grid sell-path-grid--inline" role="group" aria-label={t('listing.sellPathTitle')}>
          <SecondarySellCard
            title={t('listing.sellPathWithImei')}
            hint={t('listing.sellPathWithImeiHint')}
            iconKind="with-imei"
            onClick={() => openForm('with-imei')}
          />
          <SecondarySellCard
            title={t('listing.sellPathNoImei')}
            hint={t('listing.sellPathNoImeiHint')}
            iconKind="without-imei"
            onClick={() => setStep('choose-device')}
          />
        </div>
      </div>
    </section>
  )
}
